import { useCallback, useMemo } from 'react';
import type { UserAgendaTableDataSchemaType, GetViewFormItemsFilterRes, ViewFormItemsFilter, GetViewColumn } from 'types';
import { type TABLE_CELL_TYPE, VIEW_ITEM_STATE } from 'types/enums';
import { getCellTypeByFormType } from 'utils/agendaRecord/agendaRecord.ts';

export interface UseFilterDataSchemaProps {
  filterViewFormItems?: GetViewFormItemsFilterRes['viewFormItems'];
}

export interface UseColumnFilterDataSchemaProps {
  viewColumns?: GetViewColumn[];
}

export const useFilterDataSchema = ({ filterViewFormItems }: UseFilterDataSchemaProps) => {
  const getAllFormItems = useCallback((data?: ViewFormItemsFilter[]) => {
    if (!data) return [];

    const collectFormItems = (items: ViewFormItemsFilter[]): ViewFormItemsFilter['formItem'][] => {
      return items.flatMap((dataItem) => {
        if (!dataItem) return [];
        const { formItem, referencedFormItems } = dataItem;
        if (!formItem) return [];

        const currentFormItem = Array.isArray(formItem) ? formItem : [formItem];

        if (!referencedFormItems?.length) {
          return currentFormItem;
        }

        return [...currentFormItem, ...collectFormItems(referencedFormItems)];
      });
    };

    return collectFormItems(data);
  }, []);

  const filterDataSchema: UserAgendaTableDataSchemaType = useMemo(() => {
    const allItems = getAllFormItems(filterViewFormItems);

    return allItems
      .filter(({ viewItemState }) => [VIEW_ITEM_STATE.ENABLED_AND_FILTER, VIEW_ITEM_STATE.DISABLED_AND_FILTER].includes(viewItemState))
      .reduce<UserAgendaTableDataSchemaType>((acc, item) => {
        acc[item.formItemId ?? ''] = {
          label: item.formItemId,
          formItemDefinition: item.definition,
          type: getCellTypeByFormType(item.formItemType!) as TABLE_CELL_TYPE,
          originalType: String(item.formItemType),
          customKey: String(item.formItemTitle || item.formItemName),
          group: '',
          groupName: '',
          groupOrder: 0,
          dateMask: item.definition?.Date?.dateMask || item.definition?.date?.dateMask || '',
          isEditable: item.isEditable,
        };
        return acc;
      }, {});
  }, [filterViewFormItems, getAllFormItems]);

  return filterDataSchema;
};

export const useColumnFilterDataSchema = ({ viewColumns }: UseColumnFilterDataSchemaProps) => {
  const filterDataSchema: UserAgendaTableDataSchemaType = useMemo(() => {
    if (!viewColumns) return {};

    return viewColumns
      .filter((col) => [VIEW_ITEM_STATE.ENABLED_AND_FILTER, VIEW_ITEM_STATE.DISABLED_AND_FILTER].includes(col.state))
      .reduce<UserAgendaTableDataSchemaType>((acc, col) => {
        const key = col.columnId;

        if (!key) return acc;

        acc[key] = {
          label: key,
          type: 'text' as TABLE_CELL_TYPE,
          originalType: 'Text',
          formItemDefinition: undefined,
          customKey: col.columnName,
          group: '',
          groupName: '',
          groupOrder: 0,
          dateMask: '',
          isEditable: false,
        };

        return acc;
      }, {});
  }, [viewColumns]);

  return filterDataSchema;
};
